import { Button } from 'react-bootstrap';
import Input from '@/components/Input';
import { InvoiceDocumentWithSummary } from '@/models/gen/graphql';
import { PartialInvoiceDocumentWithSummary } from '@/api/services/invoices/searchInvoiceDocumentTable';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';
import { createComponentState } from '@/state';

export type InvoiceHistoryTableContext = {
  sorting: {
    column: string | undefined;
    direction: 'asc' | 'desc' | undefined;
  };
};
export const useInvoiceHistoryTable = createComponentState<InvoiceHistoryTableContext>({
  sorting: {
    column: 'createdAt',
    direction: 'desc',
  },
});

const InvoiceHistoryTableHeader = ({
  data,
  selected,
  onSelect,
}: {
  data: PartialInvoiceDocumentWithSummary[];
  selected: string[];
  onSelect: (id: string | string[]) => void;
}): JSX.Element => {
  const [{ sortBy, sortDirection }, setState] = useInvoiceHistoryTable(({ state, setState }) => [
    {
      sortBy: state.sorting.column,
      sortDirection: state.sorting.direction,
    },
    setState,
  ]);
  const handleSort = (column: string) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isSameColumn = sortBy === column;
    let newDirection: 'asc' | 'desc' | undefined;
    if (!isSameColumn) {
      newDirection = 'asc';
    } else if (sortDirection === 'asc') {
      newDirection = 'desc';
    } else if (sortDirection === 'desc') {
      newDirection = undefined;
    } else {
      newDirection = 'asc';
    }
    setState((current) => ({
      ...current,
      sorting: {
        column,
        direction: newDirection,
      },
    }));
  };
  const sorting = (key: string) => ({ direction: sortBy === key ? sortDirection : undefined, onSort: handleSort(key) });
  const onSelectAll = (): void => {
    onSelect(data.map((row: InvoiceDocumentWithSummary) => row.invoiceId.toString()));
  };

  return (
    <Table.Row header className="InvoiceHistoryTable-Header">
      <Table.Cell className="SELECT">
        <Input type="checkbox" onChange={onSelectAll} checked={selected.length === data.length && data.length > 0} />
      </Table.Cell>
      <Table.Cell className="EXPAND"></Table.Cell>
      <Table.Cell className="ID" sorting={sorting('amendment')}>
        NO.
      </Table.Cell>
      <Table.Cell className="INVDATE" sorting={sorting('invoiceDate')}>
        INV DATE
      </Table.Cell>
      <Table.Cell className="START" sorting={sorting('startDatetime')}>
        START
      </Table.Cell>
      <Table.Cell className="END" sorting={sorting('endDatetime')}>
        END
      </Table.Cell>
      <Table.Cell className="CLIENT" sorting={sorting('payerProviderDisplayName')}>
        CLIENT
      </Table.Cell>
      <Table.Cell className="AIRPORT" sorting={sorting('airports')}>
        AIRPORT
      </Table.Cell>
      <Table.Cell className="AMOUNT" sorting={sorting('grandTotal')}>
        AMOUNT
      </Table.Cell>
      <Table.Cell className="DUE" sorting={sorting('dueDatetime')}>
        DUE
      </Table.Cell>
      <Table.Cell className="DOWNLOAD">
        <i className="sv sv-download2 fs-4" />
      </Table.Cell>
      <Table.Cell className="AMENDED" sorting={sorting('createdAt')}>
        AMENDED
      </Table.Cell>
      <Table.Cell className="SENT">
        <Tippy content="Sent">
          <i className="fa-regular fa-envelope pointer" />
        </Tippy>
      </Table.Cell>
      <Table.Cell className="CREATOR" sorting={sorting('createdByFullName')}>
        CREATOR
      </Table.Cell>
      <Table.Cell className="SENTBY" sorting={sorting('sentByFullName')}>
        SENT BY
      </Table.Cell>
      <Table.Cell className="CREATED" sorting={sorting('createdAt')}>
        CREATED
      </Table.Cell>
      <Table.Cell className="ACTIVITY">
        <Button variant="icon" style={{ cursor: 'pointer' }}>
          <i className="sv sv-bubble-dots fs-4" />
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

export default InvoiceHistoryTableHeader;
